import React, { useState, useEffect, useCallback } from "react";
import i18n from "i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiOutlineLanguage,
  HiOutlineGlobeAlt,
  HiOutlineDocumentText,
  HiArrowPath,
  HiOutlineExclamationTriangle,
  HiOutlineCheckCircle,
  HiOutlineTrash,
  HiOutlineStar,
  HiStar,
} from "react-icons/hi2";
import AppModal from "../../common/modal/AppModal";
import { useSubtitles } from "../../../hooks/useSubtitles";
import "./_subtitlesModal.scss";

/* eslint-disable react/prop-types */

const SUBTITLE_LANGUAGES = [
  { code: "tr", label: "Türkçe" },
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "es", label: "Español" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "ar", label: "العربية" },
  { code: "ru", label: "Русский" },
  { code: "az", label: "Azərbaycanca" },
  { code: "ja", label: "日本語" },
  { code: "ko", label: "한국어" },
];

function getSubtitleLanguageLabel(code) {
  const nativeLabel = SUBTITLE_LANGUAGES.find((l) => l.code === code)?.label;
  return nativeLabel || code.toUpperCase();
}

/**
 * Editor-side port of console's SubtitlesModal — same middleware endpoints
 * (via useSubtitles), same feature set (view/generate/delete per-language
 * subtitles), reskinned with the editor's own AppModal chrome + SCSS instead
 * of console's Tailwind markup.
 */
export default function SubtitlesModal({ open, onClose, trainingId, trainingName }) {
  // `props.trainingId` (threaded down from EditorApp's `this.state.trainingId`
  // via EditorNavBar) can be stale/unset (e.g. its `-1` init placeholder)
  // depending on load timing — every other call site in this codebase reads
  // `window.idealStudioTrainingId` as the authoritative fallback instead
  // (see VideoExportTabV2's `_trainingId()`). Without this fallback the
  // subtitle endpoints got called with a missing id and generateSubtitle's
  // own guard rejected with "Missing parameters".
  const resolvedTrainingId = trainingId || window.idealStudioTrainingId;

  const {
    sceneAudios,
    sceneAudiosLoading,
    fetchSceneAudios,
    subtitleLanguages,
    subtitleTotalScenes,
    subtitleLanguagesLoading,
    fetchSubtitleLanguages,
    defaultSubtitleLanguage,
    isSavingDefault,
    setDefaultSubtitleLanguage,
    sceneSubtitle,
    sceneSubtitleLoading,
    sceneSubtitleNotFound,
    fetchSceneSubtitle,
    isGeneratingSubtitle,
    generateProgress,
    generateSubtitle,
    isDeletingSubtitle,
    deleteSubtitleLanguage,
    reset,
  } = useSubtitles(open ? resolvedTrainingId : null);

  const [generateLanguage, setGenerateLanguage] = useState("");
  const [forceCreate, setForceCreate] = useState(false);
  const [viewLanguage, setViewLanguage] = useState("");
  const [confirmDeleteLanguage, setConfirmDeleteLanguage] = useState(null);

  useEffect(() => {
    if (open && resolvedTrainingId) {
      fetchSceneAudios();
      fetchSubtitleLanguages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resolvedTrainingId]);

  useEffect(() => {
    if (!open) {
      setViewLanguage("");
      setGenerateLanguage("");
      setForceCreate(false);
      setConfirmDeleteLanguage(null);
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleViewSceneSubtitle = useCallback(
    (navId, language) => {
      const key = `${navId}_${language}`;
      if (!sceneSubtitle[key] && !sceneSubtitleLoading[key] && !sceneSubtitleNotFound[key]) {
        fetchSceneSubtitle(navId, language);
      }
    },
    [sceneSubtitle, sceneSubtitleLoading, sceneSubtitleNotFound, fetchSceneSubtitle]
  );

  useEffect(() => {
    if (viewLanguage && sceneAudios?.scenes) {
      sceneAudios.scenes.forEach((scene) => handleViewSceneSubtitle(scene.navId, viewLanguage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewLanguage, sceneAudios]);

  const handleGenerate = async () => {
    if (!generateLanguage) return;
    const result = await generateSubtitle(generateLanguage, forceCreate);
    if (!result.success && result.message) {
      window.alert(result.message);
    }
  };

  // One default at most: starring another language moves it, starring the
  // current default again clears it.
  const handleToggleDefault = async (languageCode) => {
    const next = defaultSubtitleLanguage === languageCode ? null : languageCode;
    const result = await setDefaultSubtitleLanguage(next);
    if (!result.success) {
      window.alert(
        result.message || i18n.t("subtitles.default_failed", "Varsayılan altyazı kaydedilemedi.")
      );
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteLanguage) return;
    const result = await deleteSubtitleLanguage(confirmDeleteLanguage);
    if (result.success) {
      if (viewLanguage === confirmDeleteLanguage) setViewLanguage("");
    } else if (result.message) {
      window.alert(result.message);
    }
    setConfirmDeleteLanguage(null);
  };

  return (
    <AppModal
      show={open}
      onClose={onClose}
      title={
        <span className="subtitles-modal-title">
          <HiOutlineLanguage />
          {i18n.t("subtitles.title", "Altyazılar")}
          {trainingName ? ` — ${trainingName}` : ""}
        </span>
      }
      size="lg"
      maxWidth="760px"
      backdrop="static"
      keyboard
      className="subtitlesModal"
      bodyClassName="subtitles-modal-body"
    >
      <div className="subtitles-modal-content">
        {/* Available Languages */}
        <div className="subtitles-section">
          <div className="subtitles-section-header">
            <h3>
              <HiOutlineGlobeAlt />
              {i18n.t("subtitles.available_languages", "Mevcut Diller")}
            </h3>
            <button
              type="button"
              className="subtitles-refresh-btn"
              onClick={fetchSubtitleLanguages}
              disabled={subtitleLanguagesLoading}
            >
              <HiArrowPath className={subtitleLanguagesLoading ? "spin" : ""} />
              {i18n.t("subtitles.refresh", "Yenile")}
            </button>
          </div>

          {subtitleLanguagesLoading ? (
            <div className="subtitles-chip-skeleton">
              <span />
              <span />
              <span />
            </div>
          ) : subtitleLanguages.length === 0 ? (
            <div className="subtitles-empty-box">
              <p>
                {i18n.t(
                  "subtitles.no_subtitles",
                  "Henüz altyazı üretilmedi. Altyazı üretmek için aşağıdaki formu kullanın."
                )}
              </p>
            </div>
          ) : (
            <div className="subtitles-chip-list">
              {subtitleLanguages.map((lang) => {
                const isComplete = lang.subtitleCount >= subtitleTotalScenes;
                const isPartial =
                  lang.subtitleCount > 0 && lang.subtitleCount < subtitleTotalScenes;
                const isSelected = viewLanguage === lang.languageCode;
                const isDefault = defaultSubtitleLanguage === lang.languageCode;

                return (
                  <div
                    key={lang.languageCode}
                    className={`subtitles-chip${isSelected ? " is-selected" : ""}${
                      isComplete ? " is-complete" : ""
                    }${isPartial ? " is-partial" : ""}`}
                  >
                    <button
                      type="button"
                      className="subtitles-chip-main"
                      onClick={() => setViewLanguage(lang.languageCode)}
                    >
                      {getSubtitleLanguageLabel(lang.languageCode)}
                      <span className="subtitles-chip-count">
                        {lang.subtitleCount}/{subtitleTotalScenes}
                      </span>
                      {isPartial && !isSelected && (
                        <HiOutlineExclamationTriangle className="warn" />
                      )}
                      {isComplete && !isSelected && <HiOutlineCheckCircle className="ok" />}
                    </button>
                    <button
                      type="button"
                      className={`subtitles-chip-default${isDefault ? " is-default" : ""}`}
                      title={
                        isDefault
                          ? i18n.t(
                              "subtitles.unset_default",
                              "Varsayılan altyazı — kaldırmak için tıklayın"
                            )
                          : i18n.t("subtitles.set_default", "Varsayılan altyazı yap")
                      }
                      aria-pressed={isDefault}
                      disabled={isSavingDefault}
                      onClick={() => handleToggleDefault(lang.languageCode)}
                    >
                      {isDefault ? <HiStar /> : <HiOutlineStar />}
                    </button>
                    <button
                      type="button"
                      className="subtitles-chip-delete"
                      title={i18n.t("subtitles.delete_language", "Bu dildeki altyazıları sil")}
                      onClick={() => setConfirmDeleteLanguage(lang.languageCode)}
                    >
                      <HiOutlineTrash />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {subtitleLanguages.length > 0 && (
            <p className="subtitles-default-hint">
              <HiStar />
              {i18n.t(
                "subtitles.default_hint",
                "Yıldızlı dil, önizlemede altyazı butonuna basıldığında açılır."
              )}
            </p>
          )}
        </div>

        {/* Subtitle Viewer — per scene */}
        {viewLanguage && sceneAudios?.scenes && (
          <div className="subtitles-section">
            <h3>
              <HiOutlineDocumentText />
              {i18n.t("subtitles.subtitles_for", "Şu dilin altyazıları")}{" "}
              {getSubtitleLanguageLabel(viewLanguage)}
            </h3>
            <div className="subtitles-scene-list">
              {sceneAudiosLoading ? (
                <div className="subtitles-scene-skeleton">
                  <span />
                  <span />
                  <span />
                </div>
              ) : (
                sceneAudios.scenes.map((scene, index) => {
                  const key = `${scene.navId}_${viewLanguage}`;
                  const subtitle = sceneSubtitle[key];
                  const isLoading = sceneSubtitleLoading[key];
                  const notFound = sceneSubtitleNotFound[key];

                  return (
                    <div key={scene.navId} className="subtitles-scene-row">
                      <div className="subtitles-scene-row-header">
                        <span className="subtitles-scene-index">{index + 1}</span>
                        <span className="subtitles-scene-title">{scene.sceneTitle}</span>
                      </div>
                      {isLoading ? (
                        <div className="subtitles-scene-text-skeleton">
                          <span />
                          <span />
                        </div>
                      ) : notFound ? (
                        <div className="subtitles-scene-not-found">
                          <HiOutlineExclamationTriangle />
                          {i18n.t("subtitles.not_found", "Bu sahne için altyazı bulunamadı")}
                        </div>
                      ) : subtitle ? (
                        <p className="subtitles-scene-text">
                          {subtitle.subtitleText
                            .replace(/<[^>]*>/g, "")
                            .replace(/<!--[^>]*-->/g, "")
                            .trim()}
                        </p>
                      ) : (
                        <p className="subtitles-scene-text is-empty">
                          {i18n.t("subtitles.no_subtitle_for_scene", "Bu sahne için altyazı yok")}
                        </p>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Generate Subtitles */}
        <div className="subtitles-generate-box">
          <h3>
            <HiOutlineLanguage />
            {i18n.t("subtitles.generate_title", "Altyazı Üret")}
          </h3>

          <div className="subtitles-generate-row">
            <div className="subtitles-generate-controls">
              <select
                value={generateLanguage}
                onChange={(e) => setGenerateLanguage(e.target.value)}
                disabled={isGeneratingSubtitle}
              >
                <option value="">{i18n.t("subtitles.select_language", "Dil seçin...")}</option>
                {SUBTITLE_LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.label}
                  </option>
                ))}
              </select>

              <label className="subtitles-force-checkbox">
                <input
                  type="checkbox"
                  checked={forceCreate}
                  onChange={(e) => setForceCreate(e.target.checked)}
                  disabled={isGeneratingSubtitle}
                />
                {i18n.t("subtitles.force_regenerate", "Zorla yeniden üret")}
              </label>
            </div>

            <button
              type="button"
              className="subtitles-generate-btn"
              onClick={handleGenerate}
              disabled={isGeneratingSubtitle || !generateLanguage}
            >
              {isGeneratingSubtitle ? <HiArrowPath className="spin" /> : <HiOutlineGlobeAlt />}
              {isGeneratingSubtitle
                ? i18n.t("subtitles.generating", "Üretiliyor...")
                : i18n.t("subtitles.generate_btn", "Üret")}
            </button>
          </div>

          {isGeneratingSubtitle && (
            <div className="subtitles-progress">
              <div className="subtitles-progress-label">
                <span>{i18n.t("subtitles.generating_subtitles", "Altyazılar üretiliyor...")}</span>
                <span>{generateProgress}%</span>
              </div>
              <div className="subtitles-progress-bar">
                <div style={{ width: `${generateProgress}%` }} />
              </div>
            </div>
          )}
        </div>

        {/* Confirm Delete Overlay */}
        <AnimatePresence>
          {confirmDeleteLanguage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="subtitles-confirm-overlay"
            >
              <div className="subtitles-confirm-card">
                <div className="subtitles-confirm-header">
                  <div className="subtitles-confirm-icon">
                    <HiOutlineTrash />
                  </div>
                  <div>
                    <h3>{i18n.t("subtitles.confirm_delete_title", "Altyazıları Sil")}</h3>
                    <p>{getSubtitleLanguageLabel(confirmDeleteLanguage)}</p>
                  </div>
                </div>
                <p className="subtitles-confirm-message">
                  {i18n.t(
                    "subtitles.confirm_delete_message",
                    "Bu dildeki tüm altyazıları silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
                  )}
                </p>
                <div className="subtitles-confirm-actions">
                  <button
                    type="button"
                    className="subtitles-btn subtitles-btn-ghost"
                    onClick={() => setConfirmDeleteLanguage(null)}
                    disabled={isDeletingSubtitle}
                  >
                    {i18n.t("subtitles.cancel", "İptal")}
                  </button>
                  <button
                    type="button"
                    className="subtitles-btn subtitles-btn-danger"
                    onClick={handleConfirmDelete}
                    disabled={isDeletingSubtitle}
                  >
                    {isDeletingSubtitle ? <HiArrowPath className="spin" /> : <HiOutlineTrash />}
                    {i18n.t("subtitles.delete_confirm", "Sil")}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AppModal>
  );
}
/* eslint-enable react/prop-types */
